import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import BaseCard from '../common/layout/BaseCard';
import { Tabs } from '../common/ui/Tabs';
import { Button } from '../common/ui/Button';
import Breadcrumb from '../common/layout/Breadcrumb';
import Pill from '../common/display/Pill';
import { ArrowTopRightOnSquareIcon, GlobeAltIcon, LockClosedIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { ManageCommunityMembers } from './manage/ManageCommunityMembers';
import { ManageCommunityUpdates } from './manage/ManageCommunityUpdates';
import { ManageCommunityGeneralSettings } from './manage/ManageCommunityGeneralSettings';
import { captureAppException } from '../../utils/errorCaptureUtils';
import { Community } from './types';

const TABS = [
  { id: 'community-updates', label: 'Community Updates' },
  { id: 'member-management', label: 'Member Management' },
  { id: 'general-settings', label: 'General Settings' },
];

export const ManageCommunityPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [community, setCommunity] = useState<Community | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine active tab from query param, fallback to first tab
  const tabParam = searchParams.get('tab');
  const validTabIds = TABS.map(t => t.id);
  const activeTab = validTabIds.includes(tabParam || '') ? tabParam! : TABS[0].id;

  // Breadcrumb items
  const breadcrumbItems = [
    { label: `${community?.name || 'Community'}`, href: `/communities/${id}` },
    { label: 'Manage' }
  ];

  const handleBreadcrumbNavigate = (item: any) => {
    if (item.href) navigate(item.href);
  };

  // Update URL when tab changes
  const handleTabChange = (tabId: string) => {
    navigate(`?tab=${tabId}`, { replace: true });
  };

  useEffect(() => {
    const fetchCommunity = async () => {
      if (!id) {
        setError('Community ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const { data, error } = await supabase
          .from('communities')
          .select(`
            id,
            name,
            content_visibility,
            discoverability,
            workspace:workspaces (workspace_id, workspace_name, logo_url)
          `)
          .eq('id', id)
          .single();

        if (error) throw error;
        if (!data) throw new Error('Community not found');

        // Correctly handle the workspace which might be an array
        const processedData = {
          ...data,
          workspace: data.workspace && Array.isArray(data.workspace) && data.workspace.length > 0 
            ? data.workspace[0] 
            : data.workspace,
        } as Community;

        setCommunity(processedData);
      } catch (e) {
        captureAppException(e, {
          tags: {
            context: 'manage_community_page',
            community_id: id,
            action: 'fetch_community',
          },
        });
        setError(e instanceof Error ? e.message : 'Failed to load community');
      } finally {
        setLoading(false);
      }
    };

    fetchCommunity();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-base"></div>
      </div>
    );
  }

  if (error) {
    return (
      <BaseCard>
        <div className="p-6 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </BaseCard>
    );
  }

  if (!community) {
    return (
      <BaseCard>
        <div className="p-6 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Community Not Found</h2>
          <p className="text-gray-600">The community you're looking for doesn't exist or has been removed.</p>
        </div>
      </BaseCard>
    );
  }

  const getDiscoverabilityConfig = (discoverability: string) => {
    switch (discoverability) {
      case 'public':
        return {
          text: 'Public',
          className: 'bg-green-100 text-green-700 flex items-center gap-1 text-xs py-1',
          icon: <GlobeAltIcon className="w-3 h-3" />
        };
      case 'private':
        return {
          text: 'Private',
          className: 'bg-blue-100 text-blue-700 flex items-center gap-1 text-xs py-1',
          icon: <LockClosedIcon className="w-3 h-3" />
        };
      case 'hidden':
        return {
          text: 'Hidden',
          className: 'bg-gray-100 text-gray-700 flex items-center gap-1 text-xs py-1',
          icon: <EyeSlashIcon className="w-3 h-3" />
        };
      default:
        return {
          text: 'Unknown',
          className: 'bg-gray-100 text-gray-700 text-xs py-1',
          icon: null
        };
    }
  };

  const discoverabilityConfig = getDiscoverabilityConfig(community.discoverability);

  const tabs = [
    { id: 'community-updates', label: 'Community Updates', content: <ManageCommunityUpdates communityId={community.id} /> },
    { id: 'member-management', label: 'Member Management', content: <ManageCommunityMembers communityId={community.id} /> },
    { id: 'general-settings', label: 'General Settings', content: <ManageCommunityGeneralSettings communityId={community.id} /> },
  ];

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <BaseCard overflow='visible' className="pb-8">
        <div className="flex flex-col lg:flex-row lg:h-full">
          <div className="flex-1">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} onNavigate={handleBreadcrumbNavigate} theme="light" />
            </div>
            
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 lg:mb-0 mb-2">
                  <h1 className="text-h5 font-bold text-gray-900 truncate">
                    Manage {community.name}
                  </h1>
                  <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
                    <Pill
                      text={discoverabilityConfig.text}
                      className={discoverabilityConfig.className}
                      icon={discoverabilityConfig.icon}
                    />
                  </div>
                </div>

                <div className="lg:hidden mb-3 flex items-center gap-2">
                  <Pill
                    text={discoverabilityConfig.text}
                    className={discoverabilityConfig.className}
                    icon={discoverabilityConfig.icon}
                  />
                </div>

                {/* Workspace info */}
                {community.workspace && (
                  <div className="flex items-center gap-2 mt-2">
                    {community.workspace.logo_url && (
                      <img
                        src={community.workspace.logo_url}
                        alt={`${community.workspace.workspace_name || 'Workspace'} Logo`}
                        className="h-5 w-5 rounded-full object-cover border border-gray-200"
                      />
                    )}
                    <a
                      href={`/workspace/${community.workspace.workspace_id}`} 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary-base hover:underline"
                    >
                      {community.workspace.workspace_name || 'Workspace'}
                      <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </div>

              {/* Right part: Button */}
              <div className="flex-shrink-0"> 
                <a
                  href={`/communities/${community.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button color="primary" className="flex items-center gap-x-1.5">
                    Open Community
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </BaseCard>

      {/* Tabs and Content */}
      <div>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleTabChange}
        />
        <div className="mt-6">
          {activeTabContent}
        </div>
      </div>
    </div>
  );
};
